import { classNames } from '@commenda/utils';
import { Table } from '@tanstack/react-table';
import { useState } from 'react';
import { IconButton } from '../../IconButton';
import { Icons } from '../../Icons';
import { Input } from '../../Input';
import { PaginationConfigProps } from '../types';
import { RowsPerPageSelector } from './RowsPerPageSelector';

interface UnifiedPaginationProps<TData> {
  table?: Table<TData>;
  config: PaginationConfigProps;
  onPageSizeChange?: (pageSize: number) => void;
  currentPageSize?: number;
}

export function UnifiedTablePagination<TData>({
  table,
  config,
  onPageSizeChange,
  currentPageSize,
}: UnifiedPaginationProps<TData>) {
  const [jumpToValue, setJumpToValue] = useState<string>('');

  // Handle page-based pagination
  const handlePageClick = (page: number) => {
    if (table) {
      table.setPageIndex(page - 1);
    }
  };

  const handleJumpToPage = () => {
    if (!table) return;
    const pageNumber = parseInt(jumpToValue, 10);
    const totalPages = table.getPageCount();
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      table.setPageIndex(pageNumber - 1);
      setJumpToValue('');
    }
  };

  const handleJumpToKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };

  const renderPageButtons = () => {
    if (!table) return null;

    const totalPages = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex + 1;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 4) pages.push('...');

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(currentPage + 1, totalPages - 1);

      if (currentPage <= 3) end = 4;
      if (currentPage > totalPages - 3) start = totalPages - 3;

      for (let i = start; i <= end; i += 1) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');

      pages.push(totalPages);
    }

    return pages.map((page, index) => {
      if (page === '...')
        return (
          <div
            key={`ellipsis-${index}`}
            className="h-11 w-9 rounded-md flex justify-center items-center txt-small-medium text-text-tertiary shrink-0"
          >
            <span>...</span>
          </div>
        );

      const pageNumber = +page;

      const activePage =
        (table?.getState()?.pagination?.pageIndex || 0) + 1 || 1;

      return (
        <button
          key={`page-${pageNumber}`}
          className={classNames(
            'rounded-md h-11 p-lg flex justify-center items-center transition-colors shrink-0',
            activePage === pageNumber
              ? 'bg-bg-secondary txt-small-medium text-text-primary'
              : 'bg-bg-primary txt-small-medium text-text-tertiary hover:bg-bg-primary-hover hover:text-text-tertiary-hover'
          )}
          type="button"
          onClick={() => handlePageClick(pageNumber)}
        >
          <span>{pageNumber}</span>
        </button>
      );
    });
  };

  // Cursor-based pagination
  if (config.type === 'CURSOR') {
    const showSummary =
      config.totalItems != null &&
      config.currentPage != null &&
      config.totalItems > 0;

    let summaryText: string | null = null;
    if (showSummary) {
      const start = config.currentPage! * config.pageSize + 1;
      const end = Math.min(start + config.pageSize - 1, config.totalItems!);
      summaryText = `Showing ${start.toLocaleString()}\u2013${end.toLocaleString()} of ${config.totalItems!.toLocaleString()}`;
    }

    return (
      <div
        className={classNames(
          'flex items-center gap-xl py-md bg-bg-primary justify-start',
          config.className
        )}
      >
        {summaryText && (
          <span className="txt-extra-small-medium text-text-quaternary">
            {summaryText}
          </span>
        )}
        <div className="flex items-center gap-xl py-md bg-bg-primary justify-start">
          <IconButton
            size="lg"
            icon={<Icons.ChevronLeft className="size-5" />}
            aria-label="Previous page"
            disabled={!config.hasPreviousPage}
            onClick={config.onPreviousPage}
          />

          <IconButton
            size="lg"
            icon={<Icons.ChevronRight className="size-5" />}
            aria-label="Next page"
            disabled={!config.hasNextPage}
            onClick={config.onNextPage}
          />
        </div>
      </div>
    );
  }

  // Page-based pagination
  if (config.type === 'PAGE' && table) {
    const currentPage = table.getState().pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    const totalRows = table.getFilteredRowModel().rows.length;
    const start = (currentPage - 1) * config.pageSize + 1;
    const end = Math.min(currentPage * config.pageSize, totalRows);

    return (
      <div
        className={classNames(
          'flex justify-between items-center',
          config.className
        )}
      >
        <div className="flex items-center gap-xl py-md bg-bg-primary">
          {config.showSummary && totalRows > 0 && (
            <span className="txt-extra-small-medium text-text-quaternary">
              {`Showing ${start.toLocaleString()}\u2013${end.toLocaleString()} of ${totalRows.toLocaleString()}`}
            </span>
          )}
          <div className="flex items-center gap-md">
            <IconButton
              size="lg"
              onClick={() => table.previousPage()}
              icon={<Icons.ChevronLeft className="size-5" />}
              aria-label="Previous page"
              disabled={!table.getCanPreviousPage()}
            />

            <div className="flex items-center gap-xs">
              {renderPageButtons()}
            </div>

            <IconButton
              size="lg"
              onClick={() => table.nextPage()}
              icon={<Icons.ChevronRight className="size-5" />}
              aria-label="Next page"
              disabled={!table.getCanNextPage()}
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Jump to page section */}
            {config.jumpToPage?.isEnabled && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Jump to page:</span>
                <div className="relative">
                  <Input
                    type="number"
                    value={jumpToValue}
                    onChange={(e) => setJumpToValue(e.target.value)}
                    onKeyPress={handleJumpToKeyPress}
                    placeholder={
                      config.jumpToPage.placeholder || currentPage.toString()
                    }
                    className="text-center"
                    min="1"
                    max={totalPages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {config.showRowsPerPageSelector &&
          table &&
          onPageSizeChange &&
          currentPageSize && (
            <RowsPerPageSelector
              table={table}
              config={config}
              onPageSizeChange={onPageSizeChange}
              currentPageSize={currentPageSize}
            />
          )}
      </div>
    );
  }

  return null;
}
